import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { includes } from "lodash";

import { AccessTokenService } from "services/application/AccessToken.service";
import { NavigationService } from "services/application/Navigation.service";

import { ErrorMessage } from "errors/ErrorMessage";

type ApplicationConfig = {
  BASE_URL?: string;
};

export interface ApiServiceInterface {
  get<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T>;
  post<T = any>(url: string, data?: any): Promise<T>;
  put<T = any>(url: string, data?: any): Promise<T>;
  delete<T = any>(url: string, data?: any): Promise<T>;
}

export class ApiService implements ApiServiceInterface {
  private static readonly DEFAULT_BASE_URL: string = "/api";
  private static readonly REQUEST_METHODS: {
    [key: string]: string;
  } = Object.freeze({
    GET: "get",
    POST: "post",
    PUT: "put",
    DELETE: "delete"
  });

  private _applicationConfig: ApplicationConfig;
  private _accessTokenService: AccessTokenService;
  private _navigationService: NavigationService;
  private _fetch: AxiosInstance;

  constructor(
    applicationConfig: ApplicationConfig,
    accessTokenService: AccessTokenService,
    navigationService: NavigationService
  ) {
    this._applicationConfig = applicationConfig;
    this._accessTokenService = accessTokenService;
    this._navigationService = navigationService;

    this._fetch = axios.create({
      baseURL: applicationConfig.BASE_URL || ApiService.DEFAULT_BASE_URL,
      timeout: 25000
    });
  }

  public async get<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return await this.sendRequest<T>(
      ApiService.REQUEST_METHODS.GET,
      url,
      data,
      config
    );
  }

  public async post<T = any>(url: string, data?: any): Promise<T> {
    return await this.sendRequest<T>(
      ApiService.REQUEST_METHODS.POST,
      url,
      data
    );
  }

  public async put<T = any>(url: string, data?: any): Promise<T> {
    return await this.sendRequest<T>(ApiService.REQUEST_METHODS.PUT, url, data);
  }

  public async delete<T = any>(url: string, data?: any): Promise<T> {
    return await this.sendRequest<T>(
      ApiService.REQUEST_METHODS.DELETE,
      url,
      data
    );
  }

  private async sendRequest<T>(
    method: string,
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const params: object = {};
    const { POST, PUT, DELETE } = ApiService.REQUEST_METHODS;

    if (!includes([POST, PUT, DELETE], method)) {
      Object.assign(params, data);
    }

    return await this._fetch
      .request({ method, url, data, params, ...config })
      .then<T>(({ data }: { data: T }) => Promise.resolve(data))
      .catch(({ response: { data } }) => this.handleReject<T>(data));
  }

  private handleReject<T>(data: T & ErrorMessage) {
    if (data.statusCode === 401) {
      window.location.href = "/auth/login";
    }

    return Promise.reject(data);
  }
}
